import { DocumentObject, deserialize } from '@jsonapiworld/jsonapi-serializer';
import { backendApiGateway } from '~/client/backend';
import {
  CounterpartDirection,
  CounterpartKind,
  CounterpartWithChildren,
  CounterpartsFetchParameters,
  CounterpartsMeta,
} from '~/types/counterparts';
import { isDefined } from '~/types/typeGuards';
import {
  buildJsonAPIQueryParams,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
  jsonAPICursorPaginatorBuilder,
} from '~/utils/jsonapi-query';

const PageSize = 50;

const createJsonFilters = ({
  companyId,
  direction,
  bankIdentifier,
  kind,
  search,
  type,
  allowWithParent,
  ids,
  excludeIds,
  withContactEmail,
  hasDocuments,
  supplierAccountId,
  clientAccountId,
}: CounterpartsFetchParameters) => {
  const filterBuilder = jsonAPIFilterBuilder();
  if (companyId) {
    filterBuilder.eq('company.id', companyId);
  }

  if (direction === CounterpartDirection.Client) {
    filterBuilder.eq('hasIssued', true);
  }
  if (direction === CounterpartDirection.Supplier) {
    filterBuilder.eq('hasReceived', true);
  }

  if (supplierAccountId) {
    filterBuilder.eq('supplierAccountId', supplierAccountId);
  }

  if (clientAccountId) {
    filterBuilder.eq('clientAccountId', clientAccountId);
  }

  if (bankIdentifier) {
    filterBuilder.eq('bankIdentifier', bankIdentifier);
  }

  if (search) filterBuilder.ilike('search', search);

  if (type) {
    filterBuilder.eq('identityType', type);
  }

  if (kind) {
    filterBuilder.eq('kind', kind);
  } else {
    filterBuilder.neq('kind', CounterpartKind.Virtual);
  }

  if (!allowWithParent) {
    filterBuilder.empty('parent.id', true);
  }

  if (isDefined(ids)) {
    filterBuilder.in('id', ids);
  }

  if (isDefined(excludeIds)) {
    filterBuilder.notIn('id', excludeIds);
  }

  if (withContactEmail) {
    filterBuilder.empty('contactEmail', false);
  }

  if (isDefined(hasDocuments)) {
    filterBuilder.eq('hasDocuments', hasDocuments);
  }

  return filterBuilder;
};

const fetchCounterpartsBase = async (endpoint: string, { pageParam, ...filters }: CounterpartsFetchParameters) => {
  const filterBuilder = createJsonFilters(filters);

  const includeBuilder = jsonAPIIncludeBuilder([
    'account',
    'children',
    'issuedCategory',
    'issuedSubcategory',
    'receivedCategory',
    'receivedSubcategory',
  ]);

  const paginationParamsBuilder = jsonAPICursorPaginatorBuilder();
  paginationParamsBuilder.size(PageSize);
  if (pageParam) {
    paginationParamsBuilder.cursor(pageParam);
  }

  const sortBuilder = jsonAPISortBuilder([
    { sort: 'company_name', direction: 'asc' },
    { sort: 'id', direction: 'asc' },
  ]);

  const queryParams = buildJsonAPIQueryParams(filterBuilder, includeBuilder, sortBuilder, paginationParamsBuilder);
  const { data } = await backendApiGateway.get<DocumentObject & { meta: CounterpartsMeta }>(endpoint, {
    params: queryParams,
  });
  return {
    counterparts: deserialize<CounterpartWithChildren>(data) as CounterpartWithChildren[],
    page: (data.meta as CounterpartsMeta).page,
  };
};

export const fetchCounterparts = async (filters: CounterpartsFetchParameters) => {
  return fetchCounterpartsBase('/api/v1/counterparts', filters);
};

export const fetchCounterpartsWithBeneficiaries = async (filters: CounterpartsFetchParameters) => {
  return fetchCounterpartsBase('/api/v1/counterparts/with-beneficiaries', filters);
};

interface CounterpartsMetaData {
  total: number;
}

export const fetchCounterpartsMetadata = async ({ filters }: { filters: CounterpartsFetchParameters }) => {
  const filterBuilder = createJsonFilters(filters);
  const queryParams = buildJsonAPIQueryParams(filterBuilder);
  const { data } = await backendApiGateway.get<CounterpartsMetaData>('/api/v1/counterparts/metadata', {
    params: queryParams,
  });
  return data;
};

export const fetchSuggestedCounterparts = async (filters: CounterpartsFetchParameters) => {
  const filterBuilder = createJsonFilters(filters);

  const queryParams = buildJsonAPIQueryParams(filterBuilder);
  const { data } = await backendApiGateway.get<DocumentObject>('/api/v1/counterparts/suggested', {
    params: queryParams,
  });
  return deserialize<CounterpartWithChildren>(data) as CounterpartWithChildren[];
};
